import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const SERVICES = [
  { id: "clarity_call", label: "Free 15-min Clarity Call" },
  { id: "happiness_coaching_6day", label: "6-Day Happiness Coaching — ₹6,999" },
  { id: "womens_listening", label: "Women's Listening Session — ₹999 / 45 min" },
  { id: "time_management_60", label: "Time, Priorities & Productivity — ₹1,999 / 60 min" },
  { id: "forgiveness_60", label: "The Gift of Forgiveness — ₹1,999 / 60 min" },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  service_type: "clarity_call",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.service) {
        setForm((f) => ({ ...f, service_type: e.detail.service }));
        setDone(false);
      }
    };
    window.addEventListener("prefill-service", handler);
    return () => window.removeEventListener("prefill-service", handler);
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Your request is in. Dr. Vrinda will reach out soon.");
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" className="relative py-20 lg:py-28" data-testid="booking-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-sunlit-mute mb-4">Take the first step</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-sunlit-ink leading-tight">
              Start with a <span className="italic text-sunlit-gold">free conversation</span>.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-sunlit-mute leading-relaxed">
              Please fill the form. I read every message myself and reply within 24-48 hours with the next steps and a Zoom link.
            </p>

            <div className="mt-10 rounded-3xl bg-sunlit-cream border border-stone-200/70 p-7">
              <p className="font-serif-display text-2xl text-sunlit-ink">A gentle promise.</p>
              <p className="mt-3 text-sm text-sunlit-mute leading-relaxed">
                Your words stay between us. I'll never share your details, and I'll be honest if I'm not the right coach for you.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="rounded-3xl bg-sunlit-ivory border border-stone-200/70 p-7 sm:p-9 shadow-sm"
              data-testid="booking-form"
            >
              {done ? (
                <div className="text-center py-10" data-testid="booking-success">
                  <div className="mx-auto h-14 w-14 rounded-full bg-sunlit-gold/20 flex items-center justify-center">
                    <CheckCircle2 className="h-7 w-7 text-sunlit-gold" />
                  </div>
                  <h3 className="mt-5 font-serif-display text-3xl text-sunlit-ink">Beautifully done.</h3>
                  <p className="mt-3 text-sunlit-mute">
                    Your request is with me. Watch your inbox in the next 24-48 hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="mt-7 inline-flex items-center rounded-full bg-sunlit-ink text-sunlit-ivory px-6 py-2.5 text-sm font-medium hover:bg-stone-700 transition"
                    data-testid="booking-success-reset"
                  >
                    Submit another
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Your name" required>
                      <input
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Anjali Rao"
                        className="input"
                        required
                        data-testid="booking-input-name"
                      />
                    </Field>
                    <Field label="Email" required>
                      <input
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="anjali@email.com"
                        className="input"
                        required
                        data-testid="booking-input-email"
                      />
                    </Field>
                    <Field label="Phone / WhatsApp" required>
                      <input
                        value={form.phone}
                        onChange={onChange("phone")}
                        placeholder="+91 ..."
                        className="input"
                        required
                        data-testid="booking-input-phone"
                      />
                    </Field>
                    <Field label="Service">
                      <select
                        value={form.service_type}
                        onChange={onChange("service_type")}
                        className="input"
                        data-testid="booking-select-service"
                      >
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Preferred date">
                      <input
                        type="date"
                        value={form.preferred_date}
                        onChange={onChange("preferred_date")}
                        className="input"
                        data-testid="booking-input-date"
                      />
                    </Field>
                    <Field label="Preferred time (IST)">
                      <input
                        type="time"
                        value={form.preferred_time}
                        onChange={onChange("preferred_time")}
                        className="input"
                        data-testid="booking-input-time"
                      />
                    </Field>
                  </div>

                  <Field label="Anything you'd like me to know" className="mt-5">
                    <textarea
                      value={form.message}
                      onChange={onChange("message")}
                      rows={4}
                      placeholder="A few words about what's bringing you here..."
                      className="input resize-none"
                      data-testid="booking-input-message"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-7 inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full bg-sunlit-gold hover:bg-sunlit-gold-hover disabled:opacity-60 text-sunlit-ink px-8 py-3.5 text-sm font-medium tracking-wide transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                    data-testid="booking-submit-button"
                  >
                    {submitting ? "Sending..." : "Send request"}
                    <Send className="h-4 w-4" />
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: #FDFBF7;
          border: 1px solid #E7E5E4;
          border-radius: 0.75rem;
          padding: 0.7rem 0.9rem;
          font-size: 0.95rem;
          color: #292524;
          outline: none;
          transition: border-color .15s ease, box-shadow .15s ease;
          font-family: 'Outfit', sans-serif;
        }
        .input:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.18);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-wider text-sunlit-mute mb-2">
        {label}
        {required && <span className="text-sunlit-gold ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
